<script setup lang="ts">
import IconSprite from '@/uiKit/IconSprite.vue'
import ModalDialog from '@/uiKit/ModalDialog.vue'
import { computed } from 'vue'
import type { TutorialEntry } from './useTutorialStore'

const props = withDefaults(
  defineProps<{
    tutorial: TutorialEntry
    activeTutorialId: string | null
    isParentOpen?: boolean
    showBadge?: boolean
  }>(),
  {
    isParentOpen: false,
    showBadge: false,
  },
)

const emit = defineEmits<{
  (e: 'open' | 'close'): void
}>()

const isActive = computed(() => props.activeTutorialId === props.tutorial.id)
</script>

<template>
  <button
    class="relative flex min-w-[200px] max-w-80 items-center gap-3 rounded-corner-10 border border-border-subtle p-1 text-left outline-none hover:bg-background-transparent-hovered focus-visible:bg-background-transparent-hovered active:bg-background-transparent-pressed"
    @click="emit('open')"
  >
    <div
      class="flex h-16 min-w-24 items-center justify-center rounded-corner-6 !bg-contain shadow-sm hover:shadow-md active:shadow-md"
      :style="{
        background: `url(${tutorial.thumbnail_url})`,
      }"
    >
      <div
        class="flex size-full items-center justify-center rounded-corner-6 backdrop-brightness-[0.6]"
      >
        <IconSprite
          class="text-icon-inverted-irreversible"
          icon="play-fill"
        />
      </div>
    </div>
    <div class="flex h-full grow flex-col justify-center gap-0.5">
      <div class="flex items-center gap-1">
        <div
          v-if="showBadge"
          class="size-1.5 rounded-full bg-icon-warning"
        />
        <div class="line-clamp-1 text-sm-12px-default text-text">{{ tutorial.title }}</div>
      </div>
      <p class="line-clamp-2 text-xs-11px-light text-text-subtle">
        {{ tutorial.description }}
      </p>
    </div>
    <!--
      Render one dialog per tutorial, always open but hidden, so that we don't have a noticeable
      loading time when the user clicks on a tutorial.
    -->
    <ModalDialog
      :id="`${tutorial.id}-dialog`"
      :open="isParentOpen"
      :outline="false"
      :hidden="!isActive"
      :aria-label="tutorial.title"
      :aria-hidden="!isActive"
      @close="emit('close')"
    >
      <div
        class="flex flex-col overflow-hidden rounded-corner-10 border-0 bg-surface-popover shadow-lg"
        data-test="tutorial-video-modal"
      >
        <iframe
          :key="tutorial.id"
          :width="978"
          :height="556"
          :src="tutorial.video_embed_url"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          webkitallowfullscreen
          mozallowfullscreen
          allowfullscreen
        />
      </div>
    </ModalDialog>
  </button>
</template>
